/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Car, Plus, Pencil, Trash2, X, ImagePlus, Eye, EyeOff } from "lucide-react";
import RenderAdminPage from "../RenderAdminPage";
import PageSkeleton from "../../Components/PageSkeleton";
import axiosInstance from "../../services/Axios/axios";

// Cars listed on the public Auto Scheme site (autoscheme.upnmg.com). Photos are
// uploaded with the form as multipart `files`; the first photo is the cover.
// Kept in step with VEHICLE_TYPES in the backend's vehicle.schema.ts.
const VEHICLE_TYPES = ["Saloon", "Hatchback", "SUV", "Pick-up", "Minivan"];
const MAX_PHOTO_MB = 5;

const EMPTY_FORM = {
  name: "",
  type: "",
  year: "",
  price: "",
  description: "",
  is_active: true,
};

const inputClass =
  "w-full px-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500";

const money = (value) =>
  value || value === 0
    ? `GHS ${Number(value).toLocaleString("en-GH")}`
    : "Price on request";

const errorMessage = (err) => {
  const message = err?.response?.data?.message;
  return (Array.isArray(message) ? message[0] : message) || "Something went wrong. Please try again.";
};

function VehicleModal({ vehicle, onClose, onSaved }) {
  const editing = !!vehicle;
  const [form, setForm] = useState(
    editing
      ? {
          name: vehicle.name || "",
          type: vehicle.type || "",
          year: vehicle.year ?? "",
          price: vehicle.price ?? "",
          description: vehicle.description || "",
          is_active: vehicle.is_active !== false,
        }
      : EMPTY_FORM
  );
  const [existing, setExisting] = useState(editing ? vehicle.images || [] : []);
  const [removed, setRemoved] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Preview URLs for photos picked but not yet uploaded.
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    const urls = newFiles.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [newFiles]);

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const tooBig = picked.find((f) => f.size > MAX_PHOTO_MB * 1024 * 1024);
    if (tooBig) {
      setError(`"${tooBig.name}" is larger than ${MAX_PHOTO_MB} MB.`);
      return;
    }
    setError("");
    setNewFiles((files) => [...files, ...picked].slice(0, 10));
  };

  const removeExisting = (img) => {
    setExisting((list) => list.filter((i) => i.key !== img.key));
    setRemoved((keys) => [...keys, img.key]);
  };

  const submit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append("name", form.name.trim());
    data.append("type", form.type);
    data.append("year", String(form.year).trim());
    data.append("price", String(form.price).trim());
    data.append("description", form.description.trim());
    data.append("is_active", String(form.is_active));
    if (editing && removed.length) data.append("remove_images", JSON.stringify(removed));
    newFiles.forEach((file) => data.append("files", file));

    setSaving(true);
    setError("");
    try {
      if (editing) {
        await axiosInstance.put(`/auto-scheme/vehicles/${vehicle._id}`, data);
      } else {
        await axiosInstance.post("/auto-scheme/vehicles", data);
      }
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <form
        onSubmit={submit}
        className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-gray-700">
          <h3 className="text-base font-black text-slate-800 dark:text-slate-100">
            {editing ? "Edit car" : "Add car"}
          </h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Car name *</span>
              <input name="name" value={form.name} onChange={update} required maxLength={120} placeholder="e.g. Toyota Corolla" className={inputClass} />
            </label>
            <label className="block">
              <span className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Type *</span>
              <select name="type" value={form.type} onChange={update} required className={inputClass}>
                <option value="">Select type</option>
                {VEHICLE_TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Year</span>
              <input name="year" type="number" min="1990" max="2100" value={form.year} onChange={update} placeholder="e.g. 2022" className={inputClass} />
            </label>
            <label className="block">
              <span className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Price (GHS)</span>
              <input name="price" type="number" min="0" step="any" value={form.price} onChange={update} placeholder="Leave empty for 'Price on request'" className={inputClass} />
            </label>
          </div>

          <label className="block">
            <span className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Description</span>
            <textarea name="description" value={form.description} onChange={update} rows={3} maxLength={2000} placeholder="Engine, mileage, colour, payment terms…" className={inputClass} />
          </label>

          <div>
            <span className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-2">
              Photos <span className="font-normal text-slate-400">— optional, add later if you don't have them yet · first photo is the cover · JPG, PNG or WebP · max {MAX_PHOTO_MB} MB each</span>
            </span>
            <div className="flex flex-wrap gap-3">
              {existing.map((img) => (
                <div key={img.key} className="relative w-28 h-20 rounded-lg overflow-hidden border border-slate-200 dark:border-gray-600">
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeExisting(img)} aria-label="Remove photo" className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5">
                    <X size={14} />
                  </button>
                </div>
              ))}
              {previews.map((url, index) => (
                <div key={url} className="relative w-28 h-20 rounded-lg overflow-hidden border-2 border-dashed border-indigo-300">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setNewFiles((files) => files.filter((_, i) => i !== index))} aria-label="Remove photo" className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5">
                    <X size={14} />
                  </button>
                </div>
              ))}
              <label className="w-28 h-20 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 dark:border-gray-600 text-slate-400 hover:border-indigo-400 hover:text-indigo-500 cursor-pointer text-[11px] font-semibold">
                <ImagePlus size={20} />
                Add photos
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={addFiles} className="hidden" />
              </label>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input type="checkbox" name="is_active" checked={form.is_active} onChange={update} />
            Show on the Auto Scheme site
          </label>

          {error && <p className="text-sm font-semibold text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100 dark:border-gray-700">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
            {saving ? "Saving…" : editing ? "Save changes" : "Add car"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ManageVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [modal, setModal] = useState(null); // null | "new" | vehicle
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    axiosInstance
      .get("/auto-scheme/vehicles/manage")
      .then((res) => setVehicles(res.data || []))
      .catch(() => setVehicles([]))
      .finally(() => setHasLoaded(true));

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (vehicle) => {
    setBusyId(vehicle._id);
    const data = new FormData();
    data.append("is_active", String(vehicle.is_active === false));
    try {
      await axiosInstance.put(`/auto-scheme/vehicles/${vehicle._id}`, data);
      await load();
    } catch (err) {
      alert(errorMessage(err));
    }
    setBusyId(null);
  };

  const remove = async (vehicle) => {
    if (!window.confirm(`Delete ${vehicle.name}? Its photos will be removed too.`)) return;
    setBusyId(vehicle._id);
    try {
      await axiosInstance.delete(`/auto-scheme/vehicles/${vehicle._id}`);
      await load();
    } catch (err) {
      alert(errorMessage(err));
    }
    setBusyId(null);
  };

  return (
    <RenderAdminPage title="Manage Cars">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between -mt-4 mb-5">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Cars shown on autoscheme.upnmg.com. Hidden cars stay here but members can't see them.
        </p>
        <button
          onClick={() => setModal("new")}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus size={16} /> Add car
        </button>
      </div>

      {!hasLoaded ? (
        <PageSkeleton cards={0} rows={6} cols={4} />
      ) : vehicles.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 px-4 py-16 text-center">
          <Car size={30} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">No cars yet</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Click "Add car" to list the first one.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {vehicles.map((v) => (
            <div
              key={v._id}
              className={`bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 overflow-hidden ${
                v.is_active === false ? "opacity-60" : ""
              }`}
            >
              <div className="relative aspect-[16/10] bg-slate-100 dark:bg-gray-900">
                {v.images?.[0]?.url ? (
                  <img src={v.images[0].url} alt={v.name} className="w-full h-full object-cover" />
                ) : (
                  <Car size={40} className="absolute inset-0 m-auto text-slate-300" />
                )}
                <span className="absolute left-3 top-3 text-[10px] font-bold uppercase tracking-wider bg-white/90 text-slate-700 px-2 py-1 rounded-md">
                  {v.type}
                </span>
                {v.is_active === false && (
                  <span className="absolute right-3 top-3 text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white px-2 py-1 rounded-md">
                    Hidden
                  </span>
                )}
                <span className="absolute right-3 bottom-3 text-[10px] font-bold bg-black/60 text-white px-2 py-1 rounded-md">
                  {v.images?.length || 0} photo{v.images?.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="p-4">
                <h3 className="text-sm font-black text-slate-800 dark:text-slate-100">
                  {v.name}
                  {v.year ? <span className="font-semibold text-slate-400"> · {v.year}</span> : null}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{money(v.price)}</p>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setModal(v)}
                    disabled={busyId === v._id}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700"
                  >
                    <Pencil size={13} /> Edit
                  </button>
                  <button
                    onClick={() => toggleActive(v)}
                    disabled={busyId === v._id}
                    title={v.is_active === false ? "Show on site" : "Hide from site"}
                    className="inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-40"
                  >
                    {v.is_active === false ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button
                    onClick={() => remove(v)}
                    disabled={busyId === v._id}
                    title="Delete"
                    className="inline-flex items-center justify-center px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-900/20 disabled:opacity-40"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <VehicleModal
          vehicle={modal === "new" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
          }}
        />
      )}
    </RenderAdminPage>
  );
}

export default ManageVehicles;
